import { HTMLAttributes } from "react";
import { cn } from "../../../utils/classes";
import styles from "./status-pill.module.css";

/**
 * Canonical tone names. `"success"`/`"warning"`/`"error"`/`"info"` are the
 * semantic names to reach for; the colour-named aliases (`"green"`,
 * `"orange"`, `"red"`, `"blue"`) are equivalent and exist for callers that
 * think in terms of the underlying token colour instead of the semantic.
 * Pick whichever reads better at the call site — both map to the same
 * styling.
 */
export type StatusPillTone =
  | "neutral"
  | "success"
  | "green"
  | "warning"
  | "orange"
  | "error"
  | "red"
  | "info"
  | "blue";

export type StatusPillProps = HTMLAttributes<HTMLSpanElement> & {
  /** Colour/semantic of the pill. Defaults to `"neutral"`. */
  tone?: StatusPillTone;
  /**
   * Figma Label sizes: `"xs"` 24px for tables and dense rows, `"sm"` 32px next to
   * 32px controls. Omit for the original compact pill used by existing screens.
   */
  size?: "xs" | "sm";
};

const TONE_CLASS: Record<StatusPillTone, string> = {
  neutral: "neutral",
  success: "success",
  green: "success",
  warning: "warning",
  orange: "warning",
  error: "error",
  red: "error",
  info: "info",
  blue: "info",
};

/**
 * Compact, 2px-corner status badge (legacy export name retained) for
 * things like job or run states — e.g. `<StatusPill tone="success">
 * Completed</StatusPill>`. Purely presentational: pass any label as
 * children, and a `tone` that maps to one of the shared status colour
 * pairs (green/orange/red/blue) or `"neutral"` for a plain gray badge.
 */
export function StatusPill({
  tone = "neutral",
  size,
  className,
  children,
  ...props
}: StatusPillProps) {
  return (
    <span
      className={cn(styles.pill, styles[TONE_CLASS[tone]], className)}
      data-size={size}
      {...props}
    >
      {children}
    </span>
  );
}
